import { useState } from 'react'
import { buildSharedPostURL } from './postEngagementState'
import type { SelectionSet } from '../../types/feedContract'
import { useFeedConfig } from '../../contexts/feedConfigContext'
import { useI18n } from '../../i18n'
import { copyCountLabel } from '../../branding/localizedCopy'
import './PostEngagement.css'

function ShareIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="m8.59 13.51 6.83 3.98M15.41 6.51 8.59 10.49" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m20 6-11 11-5-5" />
    </svg>
  )
}

function LinkIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  )
}

function FlameIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M13.5 2.5c.4 3-1.1 4.2-2.1 5.4-.8-1.1-1.2-2.4-1-3.8C6.8 6.2 4.8 9.4 5 13c.2 4.2 3.2 7.5 7.1 8 4.1-.5 7-3.8 6.9-8-.1-2.9-1.7-5.9-5.5-10.5Z" />
    </svg>
  )
}


async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Fall through to the selection-based path for older mobile browsers.
  }

  const field = document.createElement('textarea')
  field.value = text
  field.readOnly = true
  field.setAttribute('aria-hidden', 'true')
  field.style.position = 'fixed'
  field.style.opacity = '0'
  document.body.appendChild(field)
  field.focus()
  field.select()
  field.setSelectionRange(0, field.value.length)
  let copied = false
  try {
    copied = document.execCommand('copy')
  } finally {
    field.remove()
  }
  return copied
}



export function SharePostButton({
  postId,
  selectionSet,
  enabled,
  onShared,
}: {
  postId: string
  selectionSet?: SelectionSet | null
  enabled: boolean
  /** Receives the URL that reached the clipboard, for share attribution. */
  onShared?: (shareURL: string) => void
}) {
  const { seeMoreUrl, branding } = useFeedConfig()
  const { t } = useI18n()
  const [status, setStatus] = useState<'idle' | 'copied' | 'failed'>('idle')

  if (!enabled) return null

  const handleShare = async () => {
    const shareURL = buildSharedPostURL(
      postId,
      seeMoreUrl,
      selectionSet,
    )
    const copied = await copyText(shareURL)
    setStatus(copied ? 'copied' : 'failed')
    if (copied) onShared?.(shareURL)
  }

  const label = status === 'copied' ? t('card.shareCopied') : status === 'failed' ? t('card.shareFailed') : t('card.share')
  return (
    <button
      type="button"
      className={`post-share-button post-share-button-${status}`}
      onClick={() => void handleShare()}
      aria-label={status === 'copied' ? t('card.shareLinkCopied') : t('card.copyShareLink')}
    >
      {status === 'copied'
        ? <CheckIcon />
        : branding.assets?.shareIconUrl
          ? <img className="post-share-custom-icon" src={branding.assets.shareIconUrl} alt="" aria-hidden="true" />
          : <ShareIcon />}
      <span aria-live="polite">{label}</span>
    </button>
  )
}

export function CopyCountProof({
  count,
  label,
}: {
  count?: number
  /** The organization's label (branding `copyCountLabel`) or the card's own; unset reads "copied" in the chrome's language. */
  label?: string
}) {
  const { branding } = useFeedConfig()
  const i18n = useI18n()
  if (typeof count !== 'number') return null
  const normalizedLabel = copyCountLabel(label, count, i18n)
  const formattedCount = count.toLocaleString(i18n.locale)
  return (
    <div
      className="post-copy-count"
      aria-label={`${formattedCount} ${normalizedLabel}`}
    >
      <span className="post-copy-count-icon">
        {branding.assets?.copyCountIconUrl
          ? <img className="post-copy-count-custom-icon" src={branding.assets.copyCountIconUrl} alt="" aria-hidden="true" />
          : <FlameIcon />}
      </span>
      <strong>{formattedCount}</strong>
      <span className="post-copy-count-label">{normalizedLabel}</span>
    </div>
  )
}

export function SharedEntryBadge() {
  const { t } = useI18n()
  return (
    <span className="shared-entry-badge">
      <LinkIcon />
      {t('card.shared')}
    </span>
  )
}
